import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  CreateAIChatSavedQuestion,
  DeleteAIChatSavedQuestion,
  GetAIChatSavedQuestions,
} from '@/apis/aiChat';
import type { SavedQuestion } from '../types';
import {
  chunkCollapsedSavedQuestions,
  chunkItems,
  customResponseErrorMessage,
  getSavedQuestionsFromPayload,
} from '../utils';

export function useCopiedActions() {
  const [copiedActionKeys, setCopiedActionKeys] = useState<
    Record<string, boolean>
  >({});
  const copyResetTimersRef = useRef<Record<string, number>>({});

  const clearCopiedActions = useCallback(() => {
    Object.values(copyResetTimersRef.current).forEach((timerId) =>
      window.clearTimeout(timerId)
    );
    copyResetTimersRef.current = {};
    setCopiedActionKeys({});
  }, []);

  useEffect(() => clearCopiedActions, [clearCopiedActions]);

  const markCopied = useCallback((copyKey: string) => {
    setCopiedActionKeys((prev) => ({ ...prev, [copyKey]: true }));

    const existingTimer = copyResetTimersRef.current[copyKey];
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    copyResetTimersRef.current[copyKey] = window.setTimeout(() => {
      setCopiedActionKeys((prev) => {
        const next = { ...prev };
        delete next[copyKey];
        return next;
      });
      delete copyResetTimersRef.current[copyKey];
    }, 3000);
  }, []);

  const copyText = useCallback(
    (copyKey: string, text: string) => {
      void navigator.clipboard?.writeText(text);
      markCopied(copyKey);
    },
    [markCopied]
  );

  return {
    copiedActionKeys,
    copyText,
    clearCopiedActions,
  };
}

export function useAIChatSavedQuestions(open: boolean, question: string) {
  const [savedQuestions, setSavedQuestions] = useState<SavedQuestion[]>([]);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [showAllSaved, setShowAllSaved] = useState(false);
  const [showAllExpandedSaved, setShowAllExpandedSaved] = useState(true);

  const loadSavedQuestions = useCallback(async () => {
    try {
      const res = await GetAIChatSavedQuestions();
      setSavedQuestions(getSavedQuestionsFromPayload(res));
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.warn(
          `Saved questions load failed: ${
            error.response?.status
          } - ${customResponseErrorMessage(error) || error.message}`
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSavedQuestions([]);
      setSavingQuestion(false);
      setShowAllSaved(false);
      setShowAllExpandedSaved(true);
      return;
    }
    void loadSavedQuestions();
  }, [loadSavedQuestions, open]);

  const isCurrentQuestionSaved = useMemo(() => {
    const normalized = question.trim().toLowerCase();
    if (!normalized) return false;
    return savedQuestions.some(
      (saved) => saved.question.trim().toLowerCase() === normalized
    );
  }, [question, savedQuestions]);

  const visibleSavedQuestions = showAllSaved
    ? savedQuestions
    : savedQuestions.slice(0, 4);

  const visibleSavedRows = useMemo(
    () => chunkCollapsedSavedQuestions(visibleSavedQuestions),
    [visibleSavedQuestions]
  );

  const expandedSavedRows = useMemo(
    () =>
      chunkItems(
        showAllExpandedSaved ? savedQuestions : savedQuestions.slice(0, 4)
      ),
    [savedQuestions, showAllExpandedSaved]
  );

  // Deliberately not gated on `savingQuestion` and doesn't wait on the
  // network before updating what the user sees — the bookmark toggle's
  // visual state must never be coupled to request latency (that coupling
  // is what caused the button to flash through a disabled-looking state
  // between "saved" and "not saved"). Flip savedQuestions immediately,
  // fire the request in the background, and only touch state again to
  // roll back if it actually fails.
  //
  // pendingToggleTextsRef guards a narrower race than `savingQuestion`
  // used to: a rapid second click on the *same* not-yet-confirmed text
  // would otherwise see the optimistic entry (fake client-side id) as
  // "existing" and try to DELETE that fake id, which the backend has
  // never heard of. This silently no-ops a same-text click while its own
  // request is in flight — it's per-text, not global, and never touches
  // `disabled`/CSS, so it doesn't reintroduce the visual issue above.
  const pendingToggleTextsRef = useRef<Set<string>>(new Set());

  const toggleSaveQuestion = useCallback(async () => {
    const nextQuestion = question.trim();
    if (!nextQuestion) return;
    const key = nextQuestion.toLowerCase();
    if (pendingToggleTextsRef.current.has(key)) return;

    const existing = savedQuestions.find(
      (item) => item.question.trim().toLowerCase() === key
    );

    pendingToggleTextsRef.current.add(key);
    try {
      if (existing) {
        setSavedQuestions((prev) =>
          prev.filter((item) => item.id !== existing.id)
        );
        try {
          await DeleteAIChatSavedQuestion(existing.id);
        } catch (error: unknown) {
          setSavedQuestions((prev) => [...prev, existing]);
          if (axios.isAxiosError(error)) {
            console.warn(
              `Saved question delete failed: ${
                error.response?.status
              } - ${customResponseErrorMessage(error) || error.message}`
            );
          }
        }
        return;
      }

      const optimisticEntry: SavedQuestion = {
        id: `optimistic-${nextQuestion}`,
        question: nextQuestion,
      };
      setSavedQuestions((prev) => [...prev, optimisticEntry]);
      try {
        await CreateAIChatSavedQuestion({ question: nextQuestion });
        await loadSavedQuestions();
      } catch (error: unknown) {
        setSavedQuestions((prev) =>
          prev.filter((item) => item.id !== optimisticEntry.id)
        );
        if (axios.isAxiosError(error)) {
          console.warn(
            `Saved question create failed: ${
              error.response?.status
            } - ${customResponseErrorMessage(error) || error.message}`
          );
        }
      }
    } finally {
      pendingToggleTextsRef.current.delete(key);
    }
  }, [loadSavedQuestions, question, savedQuestions]);

  const removeSavedQuestion = useCallback(
    async (savedQuestion: SavedQuestion) => {
      if (savingQuestion) return;

      setSavingQuestion(true);
      try {
        await DeleteAIChatSavedQuestion(savedQuestion.id);
        await loadSavedQuestions();
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          console.warn(
            `Saved question delete failed: ${
              error.response?.status
            } - ${customResponseErrorMessage(error) || error.message}`
          );
        }
      } finally {
        setSavingQuestion(false);
      }
    },
    [loadSavedQuestions, savingQuestion]
  );

  return {
    savedQuestions,
    savingQuestion,
    showAllSaved,
    setShowAllSaved,
    showAllExpandedSaved,
    setShowAllExpandedSaved,
    isCurrentQuestionSaved,
    visibleSavedRows,
    expandedSavedRows,
    toggleSaveQuestion,
    removeSavedQuestion,
  };
}
